import React, { useState } from 'react';
import {
  LayoutDashboard, FileText, Users, MessageSquare, BarChart3, Settings, Plus, Edit, Trash2, Eye,
  Search, Bell, Save, Star, TrendingUp, UserPlus, CheckCircle, AlertCircle, Menu, Heart
} from 'lucide-react';
import { Helmet } from 'react-helmet';

interface Post {
  id: string; title: string; content: string; excerpt: string;
  status: 'published' | 'draft' | 'scheduled'; author: string; category: string;
  tags: string[]; publishedAt: string; views: number; likes: number; comments: number;
  featured: boolean; image?: string;
}
interface User {
  id: string; name: string; email: string; role: 'admin' | 'editor' | 'subscriber';
  status: 'active' | 'inactive'; joinedAt: string; lastLogin: string; avatar?: string;
}
interface Comment {
  id: string; postId: string; postTitle: string; author: string; email: string;
  content: string; status: 'approved' | 'pending' | 'spam'; createdAt: string;
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPostEditor, setShowPostEditor] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [posts, setPosts] = useState<Post[]>([
    { id: '1', title: 'How AI is Revolutionizing Indian Stock Market Predictions', content: 'Full article content here...', excerpt: 'Discover how artificial intelligence is transforming market predictions...', status: 'published', author: 'Dr. Rajesh Mehta', category: 'AI Insights', tags: ['AI', 'Machine Learning', 'Market Prediction'], publishedAt: '2024-01-15', views: 2543, likes: 234, comments: 45, featured: true, image: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg' },
    { id: '2', title: 'Nifty 50 Analysis: Key Levels to Watch This Week', content: 'Technical analysis content...', excerpt: 'Technical analysis of Nifty 50 with crucial support and resistance levels...', status: 'published', author: 'Priya Sharma', category: 'Market Analysis', tags: ['Nifty 50', 'Technical Analysis', 'Trading'], publishedAt: '2024-01-12', views: 1876, likes: 189, comments: 32, featured: false },
    { id: '3', title: 'Risk Management Strategies for 2024', content: 'Draft content...', excerpt: 'Essential risk management principles for retail investors...', status: 'draft', author: 'Amit Kumar', category: 'Investment Strategy', tags: ['Risk Management', 'Portfolio', 'Investment Tips'], publishedAt: '', views: 0, likes: 0, comments: 0, featured: false }
  ]);

  const [users, setUsers] = useState<User[]>([
    { id: '1', name: 'Admin User', email: 'admin@IndianStockMarketGPT.com', role: 'admin', status: 'active', joinedAt: '2023-01-01', lastLogin: '2024-01-15', avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg' },
    { id: '2', name: 'Dr. Rajesh Mehta', email: 'rajesh@IndianStockMarketGPT.com', role: 'editor', status: 'active', joinedAt: '2023-02-15', lastLogin: '2024-01-14' },
    { id: '3', name: 'Priya Sharma', email: 'priya@IndianStockMarketGPT.com', role: 'editor', status: 'active', joinedAt: '2023-03-01', lastLogin: '2024-01-13' }
  ]);

  const [comments, setComments] = useState<Comment[]>([
    { id: '1', postId: '1', postTitle: 'How AI is Revolutionizing Indian Stock Market Predictions', author: 'Investor123', email: 'investor@example.com', content: 'Great insights! This really helped me understand AI predictions better.', status: 'approved', createdAt: '2024-01-15' },
    { id: '2', postId: '1', postTitle: 'How AI is Revolutionizing Indian Stock Market Predictions', author: 'TraderPro', email: 'trader@example.com', content: 'Can you share more details about the accuracy metrics?', status: 'pending', createdAt: '2024-01-14' }
  ]);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: 'posts', label: 'Posts', icon: <FileText className="w-5 h-5" />, count: posts.length },
    { id: 'users', label: 'Users', icon: <Users className="w-5 h-5" />, count: users.length },
    { id: 'comments', label: 'Comments', icon: <MessageSquare className="w-5 h-5" />, count: comments.filter(c => c.status === 'pending').length },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> }
  ];

  const handleCreatePost = () => { setEditingPost(null); setShowPostEditor(true); };
  const handleEditPost = (post: Post) => { setEditingPost(post); setShowPostEditor(true); };
  const handleDeletePost = (postId: string) => { if (confirm('Are you sure you want to delete this post?')) setPosts(posts.filter(p => p.id !== postId)); };

  const handleSavePost = (postData: Partial<Post>) => {
    if (editingPost) {
      setPosts(posts.map(p => p.id === editingPost.id ? { ...p, ...postData } : p));
    } else {
      const newPost: Post = { id: Date.now().toString(), title: postData.title || '', content: postData.content || '', excerpt: postData.excerpt || '', status: postData.status || 'draft', author: 'Current User', category: postData.category || 'Uncategorized', tags: postData.tags || [], publishedAt: postData.status === 'published' ? new Date().toISOString().split('T')[0] : '', views: 0, likes: 0, comments: 0, featured: postData.featured || false, image: postData.image };
      setPosts([newPost, ...posts]);
    }
    setShowPostEditor(false); setEditingPost(null);
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || post.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || post.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const dashboardStats = {
    totalPosts: posts.length, publishedPosts: posts.filter(p => p.status === 'published').length,
    draftPosts: posts.filter(p => p.status === 'draft').length, totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    pendingComments: comments.filter(c => c.status === 'pending').length,
    totalViews: posts.reduce((sum, post) => sum + post.views, 0),
    totalLikes: posts.reduce((sum, post) => sum + post.likes, 0)
  };

  if (showPostEditor) {
    return <PostEditor post={editingPost} onSave={handleSavePost} onCancel={() => setShowPostEditor(false)} />;
  }

  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Sidebar */}
        <div className={`bg-white border-r border-slate-200 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} flex flex-col`}>
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              {!sidebarCollapsed && (
                <div className="flex items-center space-x-2">
                  <div className="bg-navy-500 p-2 rounded-lg"><LayoutDashboard className="w-5 h-5 text-white" /></div>
                  <span className="font-bold text-slate-900 text-sm">Admin Panel</span>
                </div>
              )}
              <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 hover:bg-slate-100 rounded text-slate-500"><Menu className="w-5 h-5" /></button>
            </div>
          </div>

          <nav className="flex-1 p-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${activeTab === item.id ? 'bg-navy-500 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                  >
                    {item.icon}
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.count !== undefined && item.count > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === item.id ? 'bg-white/20 text-white' : 'bg-red-100 text-red-600'}`}>{item.count}</span>
                        )}
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center space-x-3">
              <img src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg" alt="Admin" className="w-8 h-8 rounded-full flex-shrink-0" />
              {!sidebarCollapsed && (
                <div><div className="text-sm font-medium text-slate-900">Admin User</div><div className="text-xs text-slate-500">Administrator</div></div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="bg-white border-b border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-slate-900 capitalize font-serif">{activeTab}</h1>
              <div className="flex items-center space-x-4">
                <button className="relative p-2 text-slate-500 hover:text-slate-900">
                  <Bell className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">3</span>
                </button>
                <button className="bg-navy-500 hover:bg-navy-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium">View Site</button>
              </div>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            {activeTab === 'dashboard' && <DashboardContent stats={dashboardStats} posts={posts} users={users} comments={comments} />}
            {activeTab === 'posts' && <PostsContent posts={filteredPosts} searchTerm={searchTerm} setSearchTerm={setSearchTerm} filterStatus={filterStatus} setFilterStatus={setFilterStatus} onCreatePost={handleCreatePost} onEditPost={handleEditPost} onDeletePost={handleDeletePost} />}
            {activeTab === 'users' && <UsersContent users={users} setUsers={setUsers} />}
            {activeTab === 'comments' && <CommentsContent comments={comments} setComments={setComments} />}
            {activeTab === 'analytics' && <AnalyticsContent posts={posts} />}
            {activeTab === 'settings' && <SettingsContent />}
          </main>
        </div>
      </div>
    </>
  );
};

const DashboardContent: React.FC<{ stats: any; posts: Post[]; users: User[]; comments: Comment[] }> = ({ stats, posts, comments }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {[
        { label: 'Total Posts', value: stats.totalPosts, icon: <FileText className="w-5 h-5 text-navy-500" />, bg: 'bg-navy-50' },
        { label: 'Total Users', value: stats.totalUsers, icon: <Users className="w-5 h-5 text-green-600" />, bg: 'bg-green-50' },
        { label: 'Total Views', value: stats.totalViews.toLocaleString(), icon: <Eye className="w-5 h-5 text-purple-600" />, bg: 'bg-purple-50' },
        { label: 'Pending Comments', value: stats.pendingComments, icon: <MessageSquare className="w-5 h-5 text-amber-500" />, bg: 'bg-amber-50' },
      ].map((stat) => (
        <div key={stat.label} className="bg-white p-6 rounded-xl border border-slate-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-slate-500 mb-1">{stat.label}</p><p className="text-2xl font-bold text-slate-900">{stat.value}</p></div>
            <div className={`${stat.bg} p-3 rounded-lg`}>{stat.icon}</div>
          </div>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Recent Posts</h3></div>
        <div className="p-5 space-y-4">
          {posts.slice(0, 5).map((post) => (
            <div key={post.id} className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-slate-400" /></div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 truncate text-sm">{post.title}</h4>
                <p className="text-xs text-slate-500">{post.status} · {post.views} views</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${post.status === 'published' ? 'bg-green-100 text-green-700' : post.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{post.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Recent Comments</h3></div>
        <div className="p-5 space-y-4">
          {comments.slice(0, 5).map((comment) => (
            <div key={comment.id} className="flex items-start space-x-4">
              <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0"><MessageSquare className="w-4 h-4 text-slate-400" /></div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 text-sm">{comment.author}</h4>
                <p className="text-xs text-slate-500 truncate">{comment.content}</p>
                <p className="text-xs text-slate-400 mt-0.5">on "{comment.postTitle}"</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${comment.status === 'approved' ? 'bg-green-100 text-green-700' : comment.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{comment.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const PostsContent: React.FC<{ posts: Post[]; searchTerm: string; setSearchTerm: (t: string) => void; filterStatus: string; setFilterStatus: (s: string) => void; onCreatePost: () => void; onEditPost: (post: Post) => void; onDeletePost: (postId: string) => void }> = ({ posts, searchTerm, setSearchTerm, filterStatus, setFilterStatus, onCreatePost, onEditPost, onDeletePost }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center space-x-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search posts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-sm" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-sm">
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
        </select>
      </div>
      <button onClick={onCreatePost} className="bg-navy-500 hover:bg-navy-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
        <Plus className="w-4 h-4" /><span>Add New Post</span>
      </button>
    </div>
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>{['Title', 'Author', 'Category', 'Status', 'Date', 'Views', 'Actions'].map(h => <th key={h} className="text-left px-5 py-3.5 font-medium text-slate-700">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {posts.map((post) => (
              <tr key={post.id} className="hover:bg-slate-50">
                <td className="px-5 py-4">
                  <div className="flex items-center space-x-3">
                    {post.image && <img src={post.image} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />}
                    <div>
                      <div className="font-medium text-slate-900 flex items-center space-x-2 max-w-xs truncate">
                        <span className="truncate">{post.title}</span>
                        {post.featured && <Star className="w-3.5 h-3.5 text-amber-400 fill-current flex-shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-xs">{post.excerpt}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-slate-700 text-sm">{post.author}</td>
                <td className="px-5 py-4"><span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs">{post.category}</span></td>
                <td className="px-5 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${post.status === 'published' ? 'bg-green-100 text-green-700' : post.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{post.status}</span></td>
                <td className="px-5 py-4 text-slate-500 text-xs">{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : '—'}</td>
                <td className="px-5 py-4 text-slate-500 text-sm">{post.views.toLocaleString()}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center space-x-1">
                    <button onClick={() => onEditPost(post)} className="p-1.5 text-slate-400 hover:text-navy-500 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button className="p-1.5 text-slate-400 hover:text-green-600 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => onDeletePost(post.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const UsersContent: React.FC<{ users: User[]; setUsers: (users: User[]) => void }> = ({ users }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h2 className="text-lg font-semibold text-slate-900">Users</h2>
      <button className="bg-navy-500 hover:bg-navy-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
        <UserPlus className="w-4 h-4" /><span>Add User</span>
      </button>
    </div>
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>{['User', 'Role', 'Status', 'Joined', 'Last Login', 'Actions'].map(h => <th key={h} className="text-left px-5 py-3.5 font-medium text-slate-700">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-slate-50">
                <td className="px-5 py-4">
                  <div className="flex items-center space-x-3">
                    <img src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=1B4FD8&color=fff`} alt={user.name} className="w-9 h-9 rounded-full" />
                    <div><div className="font-medium text-slate-900">{user.name}</div><div className="text-xs text-slate-500">{user.email}</div></div>
                  </div>
                </td>
                <td className="px-5 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-red-100 text-red-700' : user.role === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{user.role}</span></td>
                <td className="px-5 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{user.status}</span></td>
                <td className="px-5 py-4 text-slate-500 text-xs">{new Date(user.joinedAt).toLocaleDateString()}</td>
                <td className="px-5 py-4 text-slate-500 text-xs">{new Date(user.lastLogin).toLocaleDateString()}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center space-x-1">
                    <button className="p-1.5 text-slate-400 hover:text-navy-500 transition-colors"><Edit className="w-4 h-4" /></button>
                    <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const CommentsContent: React.FC<{ comments: Comment[]; setComments: (comments: Comment[]) => void }> = ({ comments, setComments }) => {
  const handleApproveComment = (commentId: string) => setComments(comments.map(c => c.id === commentId ? { ...c, status: 'approved' as const } : c));
  const handleRejectComment = (commentId: string) => setComments(comments.map(c => c.id === commentId ? { ...c, status: 'spam' as const } : c));
  const handleDeleteComment = (commentId: string) => { if (confirm('Are you sure you want to delete this comment?')) setComments(comments.filter(c => c.id !== commentId)); };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Comments</h3></div>
        <div className="divide-y divide-slate-100">
          {comments.map((comment) => (
            <div key={comment.id} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0"><MessageSquare className="w-4 h-4 text-slate-400" /></div>
                    <div>
                      <h4 className="font-medium text-slate-900 text-sm">{comment.author}</h4>
                      <p className="text-xs text-slate-500">{comment.email}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${comment.status === 'approved' ? 'bg-green-100 text-green-700' : comment.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{comment.status}</span>
                  </div>
                  <p className="text-slate-700 text-sm mb-1.5">{comment.content}</p>
                  <p className="text-xs text-slate-400">On "{comment.postTitle}" · {new Date(comment.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {comment.status === 'pending' && (
                    <>
                      <button onClick={() => handleApproveComment(comment.id)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Approve"><CheckCircle className="w-4 h-4" /></button>
                      <button onClick={() => handleRejectComment(comment.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Mark as Spam"><AlertCircle className="w-4 h-4" /></button>
                    </>
                  )}
                  <button onClick={() => handleDeleteComment(comment.id)} className="p-2 text-slate-400 hover:bg-slate-50 rounded-lg transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AnalyticsContent: React.FC<{ posts: Post[] }> = ({ posts }) => {
  const totalViews = posts.reduce((sum, post) => sum + post.views, 0);
  const totalLikes = posts.reduce((sum, post) => sum + post.likes, 0);
  const totalComments = posts.reduce((sum, post) => sum + post.comments, 0);
  const avgViews = posts.length > 0 ? Math.round(totalViews / posts.length) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { label: 'Total Views', value: totalViews.toLocaleString(), icon: <Eye className="w-5 h-5 text-navy-500" />, bg: 'bg-navy-50' },
          { label: 'Total Likes', value: totalLikes.toLocaleString(), icon: <Heart className="w-5 h-5 text-red-500" />, bg: 'bg-red-50' },
          { label: 'Total Comments', value: totalComments.toLocaleString(), icon: <MessageSquare className="w-5 h-5 text-green-600" />, bg: 'bg-green-50' },
          { label: 'Avg Views/Post', value: avgViews.toLocaleString(), icon: <TrendingUp className="w-5 h-5 text-purple-600" />, bg: 'bg-purple-50' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-slate-500 mb-1">{stat.label}</p><p className="text-2xl font-bold text-slate-900">{stat.value}</p></div>
              <div className={`${stat.bg} p-3 rounded-lg`}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Top Performing Posts</h3></div>
        <div className="p-5 space-y-4">
          {posts.sort((a, b) => b.views - a.views).slice(0, 10).map((post, index) => (
            <div key={post.id} className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-medium text-slate-600 flex-shrink-0">{index + 1}</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-900 text-sm truncate">{post.title}</h4>
                <div className="flex items-center space-x-4 text-xs text-slate-500 mt-0.5">
                  <span className="flex items-center space-x-1"><Eye className="w-3.5 h-3.5" /><span>{post.views.toLocaleString()}</span></span>
                  <span className="flex items-center space-x-1"><Heart className="w-3.5 h-3.5" /><span>{post.likes}</span></span>
                  <span className="flex items-center space-x-1"><MessageSquare className="w-3.5 h-3.5" /><span>{post.comments}</span></span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs font-medium text-slate-700">{post.category}</div>
                <div className="text-xs text-slate-400">{post.publishedAt}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SettingsContent: React.FC = () => {
  const [settings, setSettings] = useState({ siteName: 'Indian Stock Market GPT', siteDescription: 'AI-powered Indian stock market intelligence', siteUrl: 'https://IndianStockMarketGPT.com', adminEmail: 'admin@IndianStockMarketGPT.com', commentsEnabled: true, registrationEnabled: false, maintenanceMode: false });
  const inputClass = "w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 text-sm";

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">General Settings</h3></div>
        <div className="p-6 space-y-5">
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Site Name</label><input type="text" value={settings.siteName} onChange={(e) => setSettings({...settings, siteName: e.target.value})} className={inputClass} /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Site Description</label><textarea value={settings.siteDescription} onChange={(e) => setSettings({...settings, siteDescription: e.target.value})} rows={2} className={`${inputClass} resize-none`} /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Site URL</label><input type="url" value={settings.siteUrl} onChange={(e) => setSettings({...settings, siteUrl: e.target.value})} className={inputClass} /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Admin Email</label><input type="email" value={settings.adminEmail} onChange={(e) => setSettings({...settings, adminEmail: e.target.value})} className={inputClass} /></div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="text-base font-semibold text-slate-900">Feature Settings</h3></div>
        <div className="p-6 space-y-5">
          {[
            { key: 'commentsEnabled', label: 'Enable Comments', desc: 'Allow users to comment on posts' },
            { key: 'registrationEnabled', label: 'User Registration', desc: 'Allow new user registrations' },
            { key: 'maintenanceMode', label: 'Maintenance Mode', desc: 'Put site in maintenance mode' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between">
              <div><h4 className="font-medium text-slate-900 text-sm">{item.label}</h4><p className="text-xs text-slate-500 mt-0.5">{item.desc}</p></div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings[item.key as keyof typeof settings] as boolean} onChange={(e) => setSettings({...settings, [item.key]: e.target.checked})} className="sr-only peer" />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-navy-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-navy-500"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end">
        <button className="bg-navy-500 hover:bg-navy-600 text-white px-6 py-2.5 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
          <Save className="w-4 h-4" /><span>Save Settings</span>
        </button>
      </div>
    </div>
  );
};

const PostEditor: React.FC<{ post: Post | null; onSave: (post: Partial<Post>) => void; onCancel: () => void }> = ({ post, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ title: post?.title || '', content: post?.content || '', excerpt: post?.excerpt || '', category: post?.category || 'Market Analysis', tags: post?.tags?.join(', ') || '', status: post?.status || 'draft', featured: post?.featured || false, image: post?.image || '' });
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); onSave({ ...formData, tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) }); };
  const inputClass = "w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900 text-sm";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900 font-serif">{post ? 'Edit Post' : 'Create New Post'}</h1>
          <div className="flex items-center space-x-3">
            <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">Cancel</button>
            <button onClick={handleSubmit} className="bg-navy-500 hover:bg-navy-600 text-white px-5 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium">
              <Save className="w-4 h-4" /><span>{post ? 'Update' : 'Publish'}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-xl border border-slate-200 p-6">
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Title</label><input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-300 text-slate-900" placeholder="Enter post title..." required /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Content</label><textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} rows={16} className={`${inputClass} font-mono`} placeholder="Write your post content here..." required /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Excerpt</label><textarea value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} rows={2} className={inputClass} placeholder="Brief description of the post..." /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><label className="block text-sm font-medium text-slate-700 mb-2">Category</label><select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className={inputClass}><option>Market Analysis</option><option>AI Insights</option><option>Trading Tips</option><option>Investment Strategy</option><option>News</option></select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-2">Status</label><select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value as any})} className={inputClass}><option value="draft">Draft</option><option value="published">Published</option><option value="scheduled">Scheduled</option></select></div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Tags</label><input type="text" value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} className={inputClass} placeholder="Enter tags separated by commas..." /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-2">Featured Image URL</label><input type="url" value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} className={inputClass} placeholder="https://example.com/image.jpg" /></div>
          <div className="flex items-center space-x-3">
            <input type="checkbox" id="featured" checked={formData.featured} onChange={(e) => setFormData({...formData, featured: e.target.checked})} className="w-4 h-4 text-navy-500 bg-white border-slate-300 rounded focus:ring-navy-400" />
            <label htmlFor="featured" className="text-sm font-medium text-slate-700">Mark as featured post</label>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPanel;
